import React, { useState } from 'react'
import { FileText, Loader2, Trash2, ExternalLink, Plus, X, Hammer, MessageSquare } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { bff, toClientError } from '../services/bff'
import { BFF_URL } from '../services/config'
import { useProjects } from '../hooks/useRag'

const run = (p) => p.then((r) => r.data).catch((e) => Promise.reject(toClientError(e)))

// Report builder (agent-user-gaps #5): compose a shareable HTML report from
// research artifacts that already have history — Q&A answers + impact analyses
// — plus freeform notes. The generated report is shown inline in an iframe
// overlay (fetched via the same API client as everything else, so it works on
// mobile) with an "open full page" link to the standalone BFF-served HTML.
export default function ReportsPanel() {
  const qc = useQueryClient()
  const projectsQuery = useProjects()
  const [project, setProject] = useState('')
  const [title, setTitle] = useState('')
  const [intro, setIntro] = useState('')
  const [picked, setPicked] = useState([]) // ordered [{type,id,label,text?}]
  const [preview, setPreview] = useState(null)     // { id, title, html }
  const [previewLoading, setPreviewLoading] = useState(false)

  // Load a report's HTML and show it inline. We fetch via the API client (not
  // window.open): a popup opened from an async callback is blocked by browsers
  // — the bug that made "Generate" do nothing, worst on mobile.
  const openPreview = async (id, reportTitle) => {
    setPreviewLoading(true)
    try {
      const html = await run(bff.get(`/api/reports/${id}`, { responseType: 'text' }))
      setPreview({ id, title: reportTitle, html })
    } catch (e) {
      setPreview({ id, title: reportTitle, html:
        `<body style="font-family:sans-serif;padding:2rem;color:#b00">Failed to load report: ${e.message}</body>` })
    } finally {
      setPreviewLoading(false)
    }
  }

  const reportsQ = useQuery({
    queryKey: ['reports'],
    queryFn: () => run(bff.get('/api/reports')),
  })
  const qaQ = useQuery({
    queryKey: ['rag', 'history', project],
    queryFn: () => run(bff.get(`/api/rag/history?project=${encodeURIComponent(project)}&limit=50`)),
    enabled: !!project,
  })
  const impactQ = useQuery({
    queryKey: ['research', 'impactHistory'],
    queryFn: () => run(bff.get('/api/research/impact/history?limit=50')),
  })

  const create = useMutation({
    mutationFn: (body) => run(bff.post('/api/reports', body)),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['reports'] })
      setPicked([]); setTitle(''); setIntro('')
      openPreview(r.id, r.title)
    },
  })
  const del = useMutation({
    mutationFn: (id) => run(bff.delete(`/api/reports/${id}`)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports'] }),
  })

  const qaEntries = qaQ.data?.entries ?? qaQ.data?.history ?? (Array.isArray(qaQ.data) ? qaQ.data : [])
  const impactRuns = (impactQ.data?.history ?? []).filter((h) => !project || h.project === project)
  const has = (type, id) => picked.some((p) => p.type === type && p.id === id)
  const toggle = (block) =>
    setPicked((cur) => has(block.type, block.id)
      ? cur.filter((p) => !(p.type === block.type && p.id === block.id))
      : [...cur, block])
  const addNote = () =>
    setPicked((cur) => [...cur, { type: 'note', id: `note-${Date.now()}`, text: '' }])
  const setNote = (id, text) =>
    setPicked((cur) => cur.map((p) => (p.id === id ? { ...p, text } : p)))
  const remove = (id) => setPicked((cur) => cur.filter((p) => p.id !== id))

  const generate = () => {
    if (!title.trim() || picked.length === 0) return
    create.mutate({
      title: title.trim(),
      project: project || undefined,
      intro: intro.trim() || undefined,
      items: picked.map((p) => p.type === 'note'
        ? { type: 'note', text: p.text }
        : { type: p.type, id: p.type === 'impact' ? Number(p.id) : p.id }),
    })
  }

  return (
    // min-w-0 on both columns: grid items default to min-width:auto, so one
    // long unbroken line (an impact run's full change text) would widen the 1fr
    // track past the viewport and push the right rail into horizontal scroll.
    <div className="grid lg:grid-cols-[1fr,340px] gap-6">
      {/* builder */}
      <div className="space-y-4 min-w-0">
        <div className="flex flex-wrap gap-2">
          <select
            value={project}
            onChange={(e) => setProject(e.target.value)}
            className="h-10 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
          >
            <option value="">Project (for answers)…</option>
            {(projectsQuery.data?.projects ?? []).map((p) => (
              <option key={p.name} value={p.name}>{p.name}</option>
            ))}
          </select>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Report title"
            className="flex-1 min-w-[180px] h-10 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
          />
        </div>
        <textarea
          value={intro}
          onChange={(e) => setIntro(e.target.value)}
          rows={2}
          placeholder="Optional intro (markdown)…"
          className="w-full rounded-xl px-4 py-2.5 text-sm bg-white border border-parchment-border text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
        />

        {/* pick answers */}
        <Picker
          title="Answers" icon={MessageSquare} empty={project ? 'No Q&A history in this project.' : 'Pick a project to list answers.'}
          items={qaEntries.map((e) => ({ type: 'qa', id: e.id, label: e.question || e.id }))}
          isOn={has} onToggle={toggle}
        />
        {/* pick impact runs */}
        <Picker
          title="Impact analyses" icon={Hammer} empty="No impact runs yet."
          items={impactRuns.map((h) => ({ type: 'impact', id: String(h.id), label: `${h.project}: ${h.query}` }))}
          isOn={has} onToggle={toggle}
        />

        <button
          onClick={addNote}
          className="inline-flex items-center gap-1.5 text-sm text-gold-deep hover:underline dark:text-gold"
        >
          <Plus className="w-4 h-4" /> Add a note block
        </button>
      </div>

      {/* right rail: selection + saved reports */}
      <div className="space-y-4 min-w-0">
        <section className="rounded-xl border border-parchment-border bg-white/70 p-4 dark:border-midnight-600 dark:bg-midnight-800/60">
          <h3 className="font-display text-sm uppercase tracking-[0.12em] text-gold-deep dark:text-gold mb-2">
            In this report ({picked.length})
          </h3>
          {picked.length === 0 && <p className="text-sm text-ink-soft dark:text-mist/50">Nothing selected yet.</p>}
          <ol className="space-y-1.5">
            {picked.map((p) => (
              <li key={p.id} className="text-sm">
                <div className="flex items-start gap-1.5">
                  <span className="text-[10px] uppercase tracking-wide text-gold-deep dark:text-gold mt-1 shrink-0">{p.type}</span>
                  {p.type === 'note'
                    ? <textarea value={p.text} onChange={(e) => setNote(p.id, e.target.value)} rows={2}
                        placeholder="Note (markdown)…"
                        className="flex-1 rounded border border-parchment-border bg-white px-2 py-1 text-xs dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist" />
                    : <span className="flex-1 min-w-0 truncate text-ink dark:text-mist" title={p.label}>{p.label}</span>}
                  <button onClick={() => remove(p.id)} className="text-ink-soft hover:text-red-600 dark:text-mist/50" aria-label="Remove">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ol>
          <button
            onClick={generate}
            disabled={!title.trim() || picked.length === 0 || create.isPending || previewLoading}
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 h-10 rounded-md text-sm font-medium border border-gold/50 text-gold-deep hover:bg-gold/10 disabled:opacity-40 dark:text-gold"
          >
            {(create.isPending || previewLoading) ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            Generate report
          </button>
          {create.isError && <p className="mt-1.5 text-xs text-red-600">{create.error?.message}</p>}
        </section>

        <section className="space-y-1.5">
          <h3 className="font-display text-sm uppercase tracking-[0.12em] text-gold-deep dark:text-gold">Saved reports</h3>
          {(reportsQ.data?.reports ?? []).length === 0 && (
            <p className="text-sm text-ink-soft dark:text-mist/50">No reports yet.</p>
          )}
          {(reportsQ.data?.reports ?? []).map((r) => (
            <div key={r.id} className="flex items-center gap-2 text-sm">
              <button
                onClick={() => openPreview(r.id, r.title)}
                className="flex-1 min-w-0 inline-flex items-center gap-1.5 truncate rounded-lg border border-parchment-border bg-white/50 px-3 py-1.5 hover:border-gold text-ink text-left dark:text-mist dark:border-midnight-600 dark:bg-midnight-800/40"
              >
                <FileText className="w-3.5 h-3.5 shrink-0 text-gold-deep dark:text-gold" />
                <span className="truncate">{r.title}</span>
                {r.stale && (
                  <span
                    title={`Repo changed since documented (${r.documented_sha?.slice(0, 7)} → ${r.current_sha?.slice(0, 7)}) — regenerate`}
                    className="shrink-0 ml-auto rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                  >
                    stale
                  </span>
                )}
              </button>
              <button onClick={() => del.mutate(r.id)} className="p-1.5 rounded text-ink-soft hover:text-red-600 dark:text-mist/50" aria-label="Delete report">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </section>
      </div>

      {/* Inline report preview — an isolated iframe (srcDoc) so the report's own
          styles don't leak, shown as a full overlay that works on mobile. */}
      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-stretch sm:items-center justify-center p-0 sm:p-6"
          onClick={() => setPreview(null)}
        >
          <div
            className="bg-white dark:bg-midnight-900 w-full sm:max-w-4xl h-full sm:h-[92vh] flex flex-col overflow-hidden sm:rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-parchment-border dark:border-midnight-600 shrink-0">
              <FileText className="w-4 h-4 shrink-0 text-gold-deep dark:text-gold" />
              <span className="flex-1 truncate text-sm font-medium text-ink dark:text-mist">{preview.title}</span>
              <a
                href={`${BFF_URL}/api/reports/${preview.id}`} target="_blank" rel="noreferrer"
                className="text-xs inline-flex items-center gap-1 text-gold-deep hover:underline dark:text-gold"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Full page
              </a>
              <button onClick={() => setPreview(null)} className="p-1 rounded text-ink-soft hover:text-red-600 dark:text-mist/50" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <iframe title={preview.title} srcDoc={preview.html} className="flex-1 w-full bg-white" />
          </div>
        </div>
      )}
    </div>
  )
}

function Picker({ title, icon: Icon, items, isOn, onToggle, empty }) {
  return (
    <section className="rounded-xl border border-parchment-border bg-white/50 p-3 dark:border-midnight-600 dark:bg-midnight-800/40">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-ink-soft dark:text-mist/50 mb-1.5">
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      {items.length === 0 && <p className="text-sm text-ink-soft dark:text-mist/50">{empty}</p>}
      <div className="space-y-1 max-h-52 overflow-auto">
        {items.map((it) => (
          <label key={`${it.type}-${it.id}`} className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={isOn(it.type, it.id)} onChange={() => onToggle(it)} className="mt-1 shrink-0 accent-gold" />
            {/* flex-1 min-w-0 lets truncate actually clip — a flex item's min-width
                defaults to its content, so a long query would stretch the row */}
            <span className="flex-1 min-w-0 truncate text-ink dark:text-mist" title={it.label}>{it.label}</span>
          </label>
        ))}
      </div>
    </section>
  )
}
